import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import CustomerCrmHubHeader from '../../pages/crm/components/CustomerCrmHubHeader';

type TabId = 'overview' | 'contacts' | 'companies' | 'deals' | 'activities' | 'telemarketing' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  hash: string;
  path?: string; // Optional path for external routes
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: '📊', hash: '#overview' },
  { id: 'contacts', label: 'Contacts', icon: '👥', hash: '#contacts' },
  { id: 'companies', label: 'Companies', icon: '🏢', hash: '#companies' },
  { id: 'deals', label: 'Deals', icon: '💰', hash: '#deals' },
  { id: 'activities', label: 'Activities', icon: '📅', hash: '#activities' },
  { id: 'telemarketing', label: 'Telemarketing', icon: '📞', hash: '', path: '/crm/telemarketing' },
  { id: 'settings', label: 'Settings', icon: '⚙️', hash: '#settings' }
];

/**
 * Persistent CRM layout that shows metrics header and tab navigation across all CRM pages
 * Used for /crm/* routes to maintain context
 */
const CrmLayoutWithMetrics: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Sync active tab with hash when on hub page or detect telemarketing route
  useEffect(() => {
    // Check if we're on the telemarketing page
    if (location.pathname.startsWith('/crm/telemarketing')) {
      setActiveTab('telemarketing');
      return;
    }
    
    if (location.pathname === '/crm') {
      const hash = location.hash || '#overview';
      const tab = TABS.find(t => t.hash === hash);
      if (tab) {
        setActiveTab(tab.id);
      }
    }
  }, [location.pathname, location.hash]);

  const handleTabClick = (tabId: TabId) => {
    const tab = TABS.find(t => t.id === tabId);
    if (tab) {
      if (tab.path) {
        navigate(tab.path);
      } else {
        navigate(`/crm${tab.hash}`);
      }
    }
  };

  // Determine if we're on the hub page, telemarketing, or a detail page
  const isHubPage = location.pathname === '/crm' || location.pathname.startsWith('/crm/telemarketing');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Persistent Metrics Header */}
        <CustomerCrmHubHeader />
        
        {/* Persistent Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6 overflow-x-auto" aria-label="CRM Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`
                    group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                    ${activeTab === tab.id && isHubPage
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                  aria-current={activeTab === tab.id && isHubPage ? 'page' : undefined}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Page Content */}
        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default CrmLayoutWithMetrics;
